
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { VisibilityRounded } from "@mui/icons-material";

import { Dialog, Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import ViewRegister from "./ViewRegister";
import ReportPrintButton from "./ReportPrintButton";
import { formatCurrency } from "../../hooks/Currency";

const RegReport = ({ _getById, _store }) => {
  const { t, i18n } = useTranslation();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [regs, setRegs] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load registers
  useEffect(() => {
    setRegs([]);

    if (!_store?.id) return;

    setLoader(true);

    const onComplete = (res) => {
      setLoader(false);

      if (res?.status === 200 && Array.isArray(res.result)) {
        setRegs(res.result);
      } else {
        setRegs([]);
      }
    };

    _getById(
      endpoints.Register + controller.GetRegsByStore,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Filter registers by opening date
  const filteredRegs = useMemo(() => {
    if (invalidRange) return [];

    return regs.filter((reg) => {
      if (!fromDate && !toDate) return true;

      if (!reg.openTime) return false;

      const registerDate = new Date(reg.openTime);

      if (Number.isNaN(registerDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(`${fromDate}T00:00:00`);
        if (registerDate < from) return false;
      }

      if (toDate) {
        const to = new Date(`${toDate}T23:59:59.999`);
        if (registerDate > to) return false;
      }

      return true;
    });
  }, [regs, fromDate, toDate, invalidRange]);

  // Prepare table data
  const tableData = useMemo(() => {
    return filteredRegs.map((reg) => ({
      ...reg,

      date: reg.openTime
        ? new Date(reg.openTime).toLocaleDateString(
            i18n.language
          )
        : "",

      openTime: reg.openTime
        ? new Date(reg.openTime).toLocaleTimeString(
            i18n.language
          )
        : "",

      closeTime: reg.closeTime
        ? new Date(reg.closeTime).toLocaleTimeString(
            i18n.language
          )
        : "",

      cashierName: [
        reg.user?.firstName,
        reg.user?.lastName,
      ]
        .filter(Boolean)
        .join(" "),

      totalSale: formatCurrency(reg.totalSale, _store),
      totalCash: formatCurrency(reg.totalCash, _store),
      totalCredit: formatCurrency(reg.totalCredit, _store),
      totalRefund: formatCurrency(reg.totalRefund, _store),
      totalVoidSale: formatCurrency(
        reg.totalVoidSale,
        _store
      ),
    }));
  }, [filteredRegs, i18n.language, _store]);

  // Preserve existing View Register functionality
  const handleView = (data) => {
    const originalRegister = regs.find(
      (reg) => Number(reg.id) === Number(data.id)
    );

    if (!originalRegister) return;

    setViewData({
      ...originalRegister,

      date: originalRegister.openTime
        ? new Date(
            originalRegister.openTime
          ).toLocaleDateString(i18n.language)
        : "",

      openTime: originalRegister.openTime
        ? new Date(
            originalRegister.openTime
          ).toLocaleTimeString(i18n.language)
        : "",

      closeTime: originalRegister.closeTime
        ? new Date(
            originalRegister.closeTime
          ).toLocaleTimeString(i18n.language)
        : "",
    });

    setShowForm(true);
  };

  // View action - screen only
  const ActionComp = (data) => (
    <Box>
      <Tooltip arrow title={t("viewRegister")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Screen columns include Actions
  const screenColumns = [
    { id: "actions", label: t("action") },
    { id: "date", label: t("date") },
    { id: "openTime", label: t("openTime") },
    { id: "closeTime", label: t("closeTime") },
    { id: "cashierName", label: t("cashierName") },
    { id: "totalSale", label: t("totalSale") },
    { id: "totalCash", label: t("cashSale") },
    { id: "totalCredit", label: t("creditSale") },
    { id: "totalRefund", label: t("refund") },
    { id: "totalVoidSale", label: t("voidSale") },
    { id: "itemCancel", label: t("itemCancel") },
  ];

  // Print columns exclude Actions
  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("registerReport")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("registerReport")}
        />
      </Box>

      {/* Normal screen table - Search and Pagination */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("registerReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={ActionComp}
          HeaderComp={() => <div />}
          tblHead={screenColumns}
          tblData={tableData}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="register-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("registerReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* All records, no Search/Pagination/Actions */}
        <Table
          printMode={true}
          HeaderComp={() => <div />}
          ActionComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View Register dialog - not included in printing */}
      {viewData && (
        <Dialog
          show={showForm}
          onClose={false}
          maxWidth="lg"
        >
          <ViewRegister
            setShowForm={setShowForm}
            viewData={viewData}
          />
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(RegReport);
